import React, { useMemo } from 'react';

/**
 * FinancialTable Component
 * 
 * Parses a pipe-formatted markdown table string and renders a production-ready
 * financial data table with dark theme styling, proper number formatting,
 * and null value handling.
 */
const FinancialTable = ({ tableString, isDarkMode, isExport = false }) => {

    // 1. Parsing Logic
    const data = useMemo(() => {
        try {
            if (!tableString) return null;

            const normalizeLine = (line) => {
                const normalized = line.replace(/^\s*[-*]\s+(?=\|)/, '').trim();
                if (normalized.startsWith('|') && !normalized.endsWith('|')) {
                    return `${normalized} |`;
                }
                return normalized;
            };

            const lines = tableString.trim().split('\n').map(normalizeLine);

            // Filter out separator lines (e.g. |---|---|)
            const contentLines = lines.filter(line => {
                const trimmed = line.trim();
                // It's a separator if it contains only pipes, dashes, colons and spaces
                return !/^\|[\s\-:|]+\|$/.test(trimmed);
            });

            if (contentLines.length < 2) return null; // Need at least header + 1 row

            const parseRow = (rowStr) => {
                // Remove leading/trailing pipes
                const inner = normalizeLine(rowStr).replace(/^\||\|$/g, '');
                return inner.split('|').map(cell => cell.trim());
            };

            const headers = parseRow(contentLines[0]);

            const rows = contentLines.slice(1).map(line => {
                let cells = parseRow(line);

                // Fix column mismatch: Pad with null if too short
                if (cells.length < headers.length) {
                    const missing = headers.length - cells.length;
                    const padding = new Array(missing).fill("-"); // Use dash as placeholder
                    cells = [...cells, ...padding];
                }

                // Truncate if too long (rare but possible)
                if (cells.length > headers.length) {
                    cells = cells.slice(0, headers.length);
                }

                return cells.map(cell => {
                    // Remove bold markdown
                    let cleanCell = cell.replace(/\*\*/g, '').trim();

                    // Handle null/empty values
                    if (cleanCell === '-' || cleanCell === '' || cleanCell.toLowerCase() === 'null') {
                        return null;
                    }

                    return cleanCell;
                });
            });

            return { headers, rows };

        } catch (err) {
            console.error("FinancialTable parsing error:", err);
            return null;
        }
    }, [tableString]);

    if (!data) {
        // Fallback if parsing fails: render as a pre block
        return (
            <pre className={`p-4 rounded-lg overflow-x-auto text-xs ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                {tableString}
            </pre>
        );
    }

    // 2. Rendering Formatters
    const formatCell = (content, rowIndex, colIndex) => {
        if (content === null) {
            return <span className="text-gray-500 italic">—</span>;
        }

        // Check if it's a number
        // Remove % for checking
        const numericStr = String(content).replace(/,/g, '').replace('%', '');
        const isNumber = !isNaN(parseFloat(numericStr)) && isFinite(numericStr);

        // First column is usually a label (Region, Category etc.), keep it left-aligned and bold
        if (colIndex === 0) {
            return <span className="font-semibold">{content}</span>;
        }

        if (isNumber) {
            // If it was a percentage originally
            if (String(content).includes('%')) {
                return content; // Keep original % string for simplicity, or parse/reformat if strict consistency needed
            }

            // Format number with Intl
            try {
                const val = parseFloat(numericStr);
                // Detect if it's an integer or float logic? 
                // The prompt asks for "16,257.1". 
                // Intl.NumberFormat('en-US') handles commas.
                return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(val);
            } catch (e) {
                return content;
            }
        }

        return content;
    };

    return (
        <div className={`w-full my-6 font-sans ${isExport ? 'break-inside-avoid' : ''}`}>
            <div
                className={`w-full rounded-lg border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                    } ${isExport ? '' : 'overflow-x-auto'}`}
            >
                <table className={`w-full border-collapse text-sm ${isExport ? 'table-fixed min-w-0' : 'min-w-[600px]'}`}>
                    <thead>
                        <tr className={`${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
                            {data.headers.map((header, idx) => (
                                <th
                                    key={idx}
                                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wider ${idx === 0 ? 'text-left' : 'text-right'} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                        }`}
                                >
                                    {header.replace(/\*\*/g, '')}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                        {data.rows.map((row, rowIdx) => (
                            <tr
                                key={rowIdx}
                                className={`transition-colors ${isDarkMode
                                        ? 'hover:bg-blue-900/20 text-slate-300'
                                        : 'hover:bg-blue-50 text-slate-700'
                                    }`}
                            >
                                {row.map((cell, cellIdx) => (
                                    <td
                                        key={cellIdx}
                                        className={`px-4 py-3 ${isExport ? 'align-top break-words whitespace-normal' : 'whitespace-nowrap'} ${cellIdx === 0 ? 'text-left' : 'text-right'} ${cellIdx !== 0 ? 'font-mono' : ''}
                                            }`}
                                    >
                                        {formatCell(cell, rowIdx, cellIdx)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FinancialTable;
